import React, { createContext, useState, useContext, useEffect } from 'react';
import unionService from '../services/unionService';
import authService from '../services/authService';

const UnionContext = createContext();

export const useUnion = () => {
  const context = useContext(UnionContext);
  if (!context) {
    throw new Error('useUnion must be used within UnionProvider');
  }
  return context;
};

export const UnionProvider = ({ children }) => {
  const [unions, setUnions] = useState([]);
  const [activeUnion, setActiveUnion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadUnions = async () => {
    if (!authService.isAuthenticated()) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await unionService.getUserUnions();
      const list = data.unions || [];
      setUnions(list);

      // Prefer the server-designated active union id
      const active = list.find(u => u.id === data.active_union_id)
        || list.find(u => u.is_active)
        || list[0]  // fallback for JWT-only users whose session wasn't set yet
        || null;
      setActiveUnion(active);

      setError(null);
    } catch (err) {
      console.error('Failed to load unions:', err);
      setError('Failed to load unions');
    } finally {
      setLoading(false);
    }
  };

  const switchUnion = async (unionId) => {
    try {
      const data = await unionService.switchUnion(unionId);
      setActiveUnion(data.active_union);

      // Reload the unions list to update the is_active flags
      await loadUnions();

      return true;
    } catch (err) {
      console.error('Failed to switch union:', err);
      setError(err.response?.data?.error || 'Failed to switch union');
      return false;
    }
  };

  useEffect(() => {
    loadUnions();
  }, []);

  // Derive the current user's role in the active union from the unions list
  const activeRole = activeUnion
    ? (unions.find(u => u.id === activeUnion.id)?.role || null)
    : null;

  // Convenience: true if the user is an admin/accountant in the active union
  const isUnionAdmin = ['ADMIN', 'ACCOUNTANT', 'BOARD_MEMBER'].includes(activeRole);

  // Forum permissions for the active union
  const forumMode = activeUnion
    ? (unions.find(u => u.id === activeUnion.id)?.forum_mode || 'DELETABLE')
    : 'DELETABLE';

  const value = {
    unions,
    activeUnion,
    activeRole,
    isUnionAdmin,
    forumMode,
    loading,
    error,
    switchUnion,
    refreshUnions: loadUnions
  };

  return (
    <UnionContext.Provider value={value}>
      {children}
    </UnionContext.Provider>
  );
};
